import { useState } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import type { HubTask, TaskCollaborator } from "../../mocks/hubTasks";

interface Props {
  task: HubTask;
  collaborator: TaskCollaborator;
  workspaceId: string;
  senderRole: string;
  onClose: () => void;
}

// 팀명 → JobRole 매핑
const TEAM_TO_ROLE: Record<string, string> = {
  "마케팅팀": "Marketing",
  "법무팀": "Legal",
  "재무팀": "Finance",
  "A&R팀": "AR",
  "공연기획팀": "PD",
  "사업팀": "Projects",
  "후원개발팀": "Fundraising",
  "회계팀": "Finance",
  "평가팀": "Evaluation",
  "에디터팀": "Editor",
  "계약팀": "Contract",
  "크리에이티브팀": "Creative",
  "AE팀": "AE",
  "제작팀": "Production",
  "데이터팀": "Data",
  "IP팀": "Contract",
  "기획팀": "PD",
};

function getReceiverRole(team: string): string {
  for (const [key, role] of Object.entries(TEAM_TO_ROLE)) {
    if (team.includes(key.replace("팀", ""))) return role;
  }
  return TEAM_TO_ROLE[team] ?? team;
}

function buildDefaultSubject(task: HubTask): string {
  return `[협업 요청] ${task.title}`;
}

function buildDefaultBody(task: HubTask, collaborator: TaskCollaborator): string {
  return `안녕하세요, ${collaborator.team} 담당자님.

저는 현재 다음 업무를 진행 중이며, 귀 팀의 협업이 필요하여 연락드립니다.

[관련 태스크]
${task.title}
기한: ${task.dueDate}

[업무 개요]
${task.detail}

[협업 요청 사항]
${collaborator.role}

빠른 시일 내에 확인 후 회신 부탁드립니다.

감사합니다.`;
}

export default function CollabMailModal({ task, collaborator, workspaceId, senderRole, onClose }: Props) {
  const { user } = useAuth();
  const [subject, setSubject] = useState(buildDefaultSubject(task));
  const [body, setBody] = useState(buildDefaultBody(task, collaborator));
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const receiverRole = getReceiverRole(collaborator.team);

  const handleSend = async () => {
    if (!subject.trim() || !body.trim() || !user) return;
    setSending(true);
    setError(null);

    const { error: err } = await supabase.from("collab_messages").insert({
      workspace_id: workspaceId,
      sender_user_id: user.id,
      sender_role: senderRole,
      receiver_role: receiverRole,
      task_id: task.id,
      task_title: task.title,
      subject: subject.trim(),
      body: body.trim(),
      is_read: false,
    });

    setSending(false);
    if (err) {
      setError("전송에 실패했습니다. 다시 시도해 주세요.");
    } else {
      setSent(true);
      setTimeout(() => onClose(), 1800);
    }
  };

  if (sent) {
    return (
      <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center">
        <div className="bg-[#141416] border border-emerald-400/30 rounded-2xl px-8 py-7 text-center shadow-xl">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-emerald-400/15 mx-auto mb-3">
            <i className="ri-check-line text-emerald-400 text-2xl"></i>
          </div>
          <p className="text-[#F2F2F2] font-semibold text-sm">협업 요청 메일이 발송되었습니다</p>
          <p className="text-[#555555] text-xs mt-1.5">
            <span className="text-emerald-400/70">{collaborator.team}</span>에서 인박스에서 확인할 수 있습니다
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-[#141416] border border-white/[0.08] rounded-2xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-400/15">
              <i className="ri-team-line text-emerald-400/80 text-sm"></i>
            </div>
            <div>
              <h3 className="text-[#F2F2F2] font-semibold text-sm">협업 요청 메일 보내기</h3>
              <p className="text-[#3D3D3D] text-[10px]">수신: {collaborator.team}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center text-[#3D3D3D] hover:text-[#888888] cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Recipient Info */}
        <div className="mx-5 mt-4 px-4 py-3 bg-emerald-400/[0.04] border border-emerald-400/10 rounded-xl">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-400/15 shrink-0">
              <i className="ri-group-line text-emerald-400/70 text-sm"></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                <span className="text-[11px] text-emerald-400/80 font-semibold">{collaborator.team}</span>
                <span className="text-[9px] text-[#3D3D3D] bg-white/[0.04] border border-white/[0.06] px-1.5 py-0.5 rounded">
                  {receiverRole}
                </span>
              </div>
              <p className="text-[#555555] text-[11px]">{collaborator.role}</p>
            </div>
          </div>
          {/* Task badge */}
          <div className="mt-2.5 flex items-center gap-1.5 pl-11">
            <i className="ri-task-line text-[#3D3D3D] text-[10px]"></i>
            <span className="text-[#444444] text-[10px] truncate">연관 태스크: {task.title}</span>
          </div>
        </div>

        {/* Form */}
        <div className="px-5 pt-4 pb-2 space-y-3">
          <div>
            <label className="text-[#444444] text-[11px] block mb-1.5">제목</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full bg-white/[0.03] text-[#DDDDDD] text-sm rounded-lg px-3 py-2.5 outline-none border border-white/[0.07] focus:border-emerald-400/30 placeholder-[#333333]"
            />
          </div>

          <div>
            <label className="text-[#444444] text-[11px] block mb-1.5">내용</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={500}
              rows={10}
              className="w-full bg-white/[0.03] text-[#CCCCCC] text-xs leading-relaxed rounded-lg px-3 py-2.5 outline-none border border-white/[0.07] focus:border-emerald-400/30 resize-none"
            />
            <div className="flex justify-between items-center mt-0.5">
              <p className="text-[#2A2A2A] text-[10px]">협업 파트너의 인박스로 즉시 전달됩니다</p>
              <span className="text-[#2A2A2A] text-[10px]">{body.length}/500</span>
            </div>
          </div>
        </div>

        {error && (
          <p className="mx-5 text-red-400/70 text-xs bg-red-400/5 border border-red-400/15 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        {/* Actions */}
        <div className="px-5 pb-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-[#555555] text-sm border border-white/[0.07] hover:border-white/[0.15] transition-colors cursor-pointer whitespace-nowrap"
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={!subject.trim() || !body.trim() || sending}
            className="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            {sending ? (
              <>
                <div className="w-3.5 h-3.5 border border-white/40 border-t-white rounded-full animate-spin"></div>
                발송 중...
              </>
            ) : (
              <>
                <i className="ri-send-plane-fill"></i>
                협업 요청 발송
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
